import {
  Accordion,
  Badge,
  Box,
  Container,
  Group,
  SimpleGrid,
  Text,
} from '@mantine/core'
import { type ReactQuery } from 'cms-api'
import Image from 'next/image'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Element } from 'react-scroll'
import slugify from 'slugify'
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter'
import { ProductThumbnailCard } from 'components/ProductThumbnail'
import { getTypesenseConfigurationOptions } from 'utils/typesenseClient'
import { ResultLabels } from './ResultLabels'

import { type ImageSize } from './types'

export type { ImageSize }

const adapter = new TypesenseInstantsearchAdapter({
  server: getTypesenseConfigurationOptions(),
  additionalSearchParameters: {
    query_by: 'title,embedding',
  },
})

const typesense = adapter.typesenseClient

export const getProductSlug = ({ id, title }: { id: number; title: string }) =>
  `${slugify(title, { lower: true, strict: true })}-p${id}`

const getUniqueDetectedProducts = (
  detectedProducts: ReactQuery.ImageSearchLabelsItem['detectedProducts']
) => {
  const uniqueProductsMap: Record<
    number,
    ReactQuery.ImageSearchLabelsItem['detectedProducts'][0]
  > = {}

  detectedProducts.forEach((detectedProduct) => {
    uniqueProductsMap[detectedProduct.product.documentId] = detectedProduct
  })

  return Object.values(uniqueProductsMap)
}

const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

export const ResultsView = ({
  results,
  confidence,
  vectorDistance,
}: {
  results: ReactQuery.ImageSearch
  confidence: number
  vectorDistance: number
}) => {
  const [imageSize, setImageSize] = useState<ImageSize>({
    width: 0,
    height: 0,
  })

  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [tsProducts, setTsProducts] = useState<Record<string, any>>({})
  const [hovering, setHovering] = useState<number | undefined>(undefined)

  const labelsWithProducts: ReactQuery.ImageSearchLabelsItem[] = useMemo(() => {
    if (!results?.labels) {
      return []
    }
    // 1. Filter labels by confidence AND filter detectedProducts by vector distance
    const filtered = results.labels
      .filter((label) => label.confidence >= confidence)
      .map((label) => ({
        ...label,
        detectedProducts: label.detectedProducts.filter(
          (dp) => dp.vectorDistance <= vectorDistance
        ),
      }))
      // 2. After filtering detectedProducts, remove labels with no products left
      .filter((label) => label.detectedProducts.length > 0)

    // 3. Group + merge unique products
    const merged = Object.values(
      filtered.reduce(
        (acc, label) => {
          const key = label.name

          if (!acc[key]) {
            acc[key] = {
              ...label,
              detectedProducts: [...label.detectedProducts],
            }
          } else {
            label.detectedProducts.forEach((dp) => {
              const doesExists = acc[key].detectedProducts.some(
                (e) => e.product.id === dp.product.id
              )
              if (!doesExists) {
                acc[key].detectedProducts.push(dp)
              }
            })
          }

          return acc
        },
        {} as Record<string, ReactQuery.ImageSearchLabelsItem>
      )
    )
    // 3. SORT detectedProducts inside each label
    const sorted = merged.map((label) => ({
      ...label,
      detectedProducts: getUniqueDetectedProducts([
        ...label.detectedProducts,
      ]).sort((a, b) => a.vectorDistance - b.vectorDistance),
    }))

    return sorted
  }, [results.labels, confidence, vectorDistance])

  const skus = useMemo(
    () =>
      labelsWithProducts.flatMap((label) =>
        label.detectedProducts.map((p) => (p.product as { sku: string }).sku)
      ),
    [labelsWithProducts]
  )

  useEffect(() => {
    if (!skus.length || !results) {
      return
    }

    const fetchProducts = async () => {
      const SKU_CHUNK_SIZE = 250
      const skuChunks = chunkArray(skus, SKU_CHUNK_SIZE)

      const searches = skuChunks.map((chunk) => ({
        collection: 'products-v5',
        q: '*',
        query_by: 'title,embedding',
        filter_by: `sku:=[${chunk.map((s) => `"${s}"`).join(',')}]`,
        per_page: chunk.length, // always ≤ 250
        include_fields:
          'title,imageUrl,imageAlt,minPrice,maxPrice,legacyProductId,brand,sku',
      }))

      const res = await typesense.multiSearch.perform({ searches })

      const allHits = res.results.flatMap((r: any) => r.hits ?? [])

      const productBySku = Object.fromEntries(
        allHits.map((h: any) => [h.document.sku, h.document])
      )

      setTsProducts(productBySku)
    }

    fetchProducts()
  }, [skus, results])

  useEffect(() => {
    const updateSize = () => {
      if (imageRef.current) {
        const { width, height } = imageRef.current.getBoundingClientRect()
        setImageSize({ width, height })
      }
    }

    updateSize()

    // Update on window resize
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [results.image?.url])

  const hoveringEmbedding =
    hovering !== undefined ? labelsWithProducts[hovering] : undefined

  return (
    <Box>
      <Group justify="center">
        {results.image?.url && (
          <Box
            ref={containerRef}
            pos="relative"
            w="50%"
            maw={500} // optional max width
            style={{ overflow: 'hidden' }}
          >
            <Image
              ref={imageRef}
              src={results.image?.url || ''}
              alt="Uploaded"
              sizes="100vw"
              width="0"
              height="0"
              style={{
                width: '100%',
                height: 'auto',
                ...(hoveringEmbedding
                  ? {
                      transition: 'opacity 0.4s ease',
                      opacity: 0.2,
                    }
                  : {
                      transition: 'opacity 0.4s ease',
                      opacity: 1,
                    }),
              }}
              onLoad={() => {
                if (imageRef.current) {
                  const { width, height } =
                    imageRef.current.getBoundingClientRect()
                  setImageSize({ width, height })
                }
              }}
            />
            <Image
              ref={imageRef}
              src={results.image?.url || ''}
              alt="Uploaded"
              sizes="100vw"
              width="0"
              height="0"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: 'auto',
                zIndex: 2,
                ...(hoveringEmbedding
                  ? {
                      transition: 'opacity 0.4s ease',
                      opacity: 1,
                      borderRadius: 4,
                      clipPath: `polygon(${hoveringEmbedding.boundingBox.left * 100}% ${
                        hoveringEmbedding.boundingBox.top * 100
                      }%, ${
                        (hoveringEmbedding.boundingBox.left +
                          hoveringEmbedding.boundingBox.width) *
                        100
                      }% ${hoveringEmbedding.boundingBox.top * 100}%, ${
                        (hoveringEmbedding.boundingBox.left +
                          hoveringEmbedding.boundingBox.width) *
                        100
                      }% ${
                        (hoveringEmbedding.boundingBox.top +
                          hoveringEmbedding.boundingBox.height) *
                        100
                      }%, ${hoveringEmbedding.boundingBox.left * 100}% ${
                        (hoveringEmbedding.boundingBox.top +
                          hoveringEmbedding.boundingBox.height) *
                        100
                      }%)`,
                    }
                  : {
                      transition: 'opacity 0.4s ease',
                      opacity: 0,
                    }),
              }}
              onLoad={() => {
                if (imageRef.current) {
                  const { width, height } =
                    imageRef.current.getBoundingClientRect()
                  setImageSize({ width, height })
                }
              }}
            />

            {/* Overlay layer */}
            {imageSize.height > 0 && (
              <ResultLabels
                labels={labelsWithProducts}
                imageSize={imageSize}
                onHovering={setHovering}
              />
            )}
          </Box>
        )}
      </Group>
      {results.image?.url && Object.keys(tsProducts).length > 0 && (
        <Container size="lg">
          <Text fz={30} fw={500} my={20}>
            Results
          </Text>
          {labelsWithProducts && labelsWithProducts.length === 1 ? (
            <Element
              name={labelsWithProducts[0].name}
              id={labelsWithProducts[0].name}
            >
              <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg" my={24}>
                {labelsWithProducts[0].detectedProducts?.map(
                  (product, prodIdx: number) => {
                    const { sku } = product.product as { sku: string }
                    const tsProduct = tsProducts[sku]
                    return (
                      tsProduct && (
                        <Box key={prodIdx}>
                          <ProductThumbnailCard
                            price={tsProduct.minPrice}
                            title={tsProduct.title}
                            brand={tsProduct.brand}
                            url={`https://theexpert.com/shop/products/${getProductSlug(
                              {
                                id: tsProduct.legacyProductId,
                                title: tsProduct.title,
                              }
                            )}`}
                            productImageUrl={tsProduct.imageUrl}
                            productImageAlt={tsProduct.imageAlt}
                            priceRange={{
                              min: tsProduct.minPrice,
                              max: tsProduct.maxPrice,
                            }}
                            productId={tsProduct.legacyProductId}
                            variantId={tsProduct.legacyVariantId}
                            tradePrice={tsProduct.tradePrice}
                            breakoutOptionCount={tsProduct.breakoutOptionCount}
                            shouldDisplayBlackFridayBadge={false}
                          />
                        </Box>
                      )
                    )
                  }
                )}
              </SimpleGrid>
            </Element>
          ) : (
            <Accordion
              mt="lg"
              multiple
              defaultValue={labelsWithProducts.map(
                (embedding, index) => `${embedding.name}-${index}`
              )}
            >
              {labelsWithProducts.map((embedding, index) => (
                <Element
                  key={index}
                  name={`${embedding.name}-${index}`}
                  id={`${embedding.name}-${index}`}
                >
                  <Accordion.Item value={`${embedding.name}-${index}`}>
                    <Accordion.Control>
                      {embedding.name}
                      <Badge ml="sm">
                        {embedding.detectedProducts
                          ?.map(
                            ({ product }) =>
                              tsProducts[(product as { sku: string }).sku]
                          )
                          ?.filter(Boolean).length ?? 0}
                      </Badge>
                    </Accordion.Control>
                    <Accordion.Panel p={0}>
                      {embedding.detectedProducts &&
                        embedding.detectedProducts.length > 0 && (
                          <SimpleGrid
                            cols={{ base: 2, md: 4 }}
                            spacing="lg"
                            my={24}
                          >
                            {embedding.detectedProducts.map(
                              (product, prodIdx: number) => {
                                const { sku } = product.product as {
                                  sku: string
                                }
                                const tsProduct = tsProducts[sku]

                                return (
                                  tsProduct && (
                                    <Box key={prodIdx}>
                                      <ProductThumbnailCard
                                        price={tsProduct.minPrice}
                                        title={tsProduct.title}
                                        brand={tsProduct.brand}
                                        url={`https://theexpert.com/shop/products/${getProductSlug(
                                          {
                                            id: tsProduct.legacyProductId,
                                            title: tsProduct.title,
                                          }
                                        )}`}
                                        productImageUrl={tsProduct.imageUrl}
                                        productImageAlt={tsProduct.imageAlt}
                                        priceRange={{
                                          min: tsProduct.minPrice,
                                          max: tsProduct.maxPrice,
                                        }}
                                        productId={tsProduct.legacyProductId}
                                        variantId={tsProduct.legacyVariantId}
                                        tradePrice={tsProduct.tradePrice}
                                        breakoutOptionCount={
                                          tsProduct.breakoutOptionCount
                                        }
                                        shouldDisplayBlackFridayBadge={false}
                                      />
                                    </Box>
                                  )
                                )
                              }
                            )}
                          </SimpleGrid>
                        )}
                    </Accordion.Panel>
                  </Accordion.Item>
                </Element>
              ))}
            </Accordion>
          )}
        </Container>
      )}
    </Box>
  )
}
